/**
 * Was ein neuer Stundenexport am Bestand ändert, mit einer Entscheidung je Eintrag.
 *
 * Keine Vorauswahl: welcher Stand stimmt, weiß nur, wer den Dienstplan kennt.
 * Ein vorausgewählter „neuer Stand" wanderte durch schnelles Klicken als
 * geprüfte Tatsache ins Audit-Log. Für viele gleichartige Fälle gibt es
 * „Alle" — auch das ein bewusster Klick.
 */
import { Link } from "react-router-dom";
import { shortDate } from "../lib/format";
import {
  entscheidbar,
  offen,
  schichtText,
  tagHinweis,
  type Aenderung,
  type Entscheidung,
  type Pruefung,
} from "../lib/importPruefung";
import { Info } from "./ui";

export function ImportAenderungen({
  aenderungen,
  entscheidungen,
  onChange,
}: {
  aenderungen: readonly Aenderung[];
  entscheidungen: Readonly<Record<string, Entscheidung>>;
  onChange: (next: Record<string, Entscheidung>) => void;
}) {
  if (aenderungen.length === 0) return null;
  const setAlle = (e: Entscheidung) =>
    onChange(Object.fromEntries(aenderungen.filter(entscheidbar).map((a) => [a.key, e])));
  const rest = offen(aenderungen, entscheidungen);

  return (
    <div className="mt-4">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
        <strong>
          {aenderungen.length === 1
            ? "Ein Eintrag weicht vom Bestand ab."
            : `${aenderungen.length} Einträge weichen vom Bestand ab.`}
        </strong>
        <Info text="Verglichen wird je Person, Tag und Bereich. Neuer Stand: die App korrigiert die Stunden, mit Grund im Audit-Log. Die ursprünglich importierte Zeile bleibt erhalten. Bisherigen behalten: der neue Export wird für diesen Eintrag nicht übernommen." />
        {rest > 0 ? <span className="chip-warn">{rest} noch zu entscheiden</span> : <span className="chip-ok">alle entschieden</span>}
        <span className="ml-auto flex gap-1">
          <button className="btn-ghost" onClick={() => setAlle("neu")}>
            Alle: neuen Stand
          </button>
          <button className="btn-ghost" onClick={() => setAlle("alt")}>
            Alle: bisherigen behalten
          </button>
        </span>
      </div>
      <div className="card overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>Person</th>
              <th>Tag</th>
              <th>Bereich</th>
              <th>Bisher</th>
              <th>Neuer Export</th>
              <th className="text-right">Was gilt?</th>
            </tr>
          </thead>
          <tbody>
            {aenderungen.map((a) => {
              const e = entscheidungen[a.key];
              return (
                <tr key={a.key}>
                  <td>
                    <div>{a.name}</div>
                    <div className="font-mono text-xs text-muted">{a.personalNr}</div>
                  </td>
                  <td className="whitespace-nowrap">
                    <Link to={`/betriebstage/${a.businessDate}`} className="hover:text-accent">
                      {shortDate(a.businessDate)}
                    </Link>
                    {a.tagStatus === "APPROVED" ? <div className="chip-warn mt-1">freigegeben</div> : null}
                    {a.tagStatus === "LOCKED" ? <div className="chip-muted mt-1">ausgezahlt</div> : null}
                  </td>
                  <td className="text-muted">{a.workArea}</td>
                  <td className="whitespace-nowrap font-mono text-xs">
                    {a.alt.map((s, i) => (
                      <div key={i}>{schichtText(s)}</div>
                    ))}
                  </td>
                  <td className="whitespace-nowrap font-mono text-xs">
                    {a.neu.length === 0 ? (
                      <span className="font-sans text-warn">fehlt im neuen Export</span>
                    ) : (
                      a.neu.map((s, i) => <div key={i}>{schichtText(s)}</div>)
                    )}
                  </td>
                  <td className="text-right">
                    {entscheidbar(a) ? (
                      <span className="inline-flex gap-1">
                        <button
                          className={e === "neu" ? "btn-primary" : "btn-ghost"}
                          aria-pressed={e === "neu"}
                          onClick={() => onChange({ ...entscheidungen, [a.key]: "neu" })}
                        >
                          {a.art === "entfernt" ? "Auf 0 setzen" : "Neuer Stand"}
                        </button>
                        <button
                          className={e === "alt" ? "btn-primary" : "btn-ghost"}
                          aria-pressed={e === "alt"}
                          onClick={() => onChange({ ...entscheidungen, [a.key]: "alt" })}
                        >
                          Bisherigen behalten
                        </button>
                      </span>
                    ) : (
                      <span className="text-xs text-muted">bleibt, Tag ist ausgezahlt</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Tage, die schon freigegeben oder ausgezahlt sind, und was der Import dort bewirkt. */
export function BetroffeneTage({ tage }: { tage: Pruefung["betroffeneTage"] }) {
  if (tage.length === 0) return null;
  return (
    <ul className="mt-3 space-y-1 rounded border border-warn/30 bg-warn/5 px-3 py-2 text-xs">
      {tage.map((t) => (
        <li key={t.datum}>
          <Link to={`/betriebstage/${t.datum}`} className="underline-offset-2 hover:underline">
            {tagHinweis(t)}
          </Link>
        </li>
      ))}
    </ul>
  );
}
